import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@/gf/lib/yupResolver'

import { useUpdateVendorContactMutation, VendorContact } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import ModalForm from '@/gf/components/ModalForm'
import PhoneInput from '@/gf/components/PhoneInput'
import TextField from '@/gf/components/TextField'
import GraphQL from '@/gf/modules/GraphQL'
import stopPropagate from '@/gf/modules/stopPropagate'

interface ContactForm {
  name: string
  email: string
  phoneNumber: string
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email().required('Email is required'),
  phoneNumber: yup.string(),
})

const EditContactModal = ({
  vendorId,
  vendorContact,
  open,
  onClose,
  onAfterSave,
}: {
  vendorId: string
  vendorContact: VendorContact
  open: boolean
  onClose: () => void
  onAfterSave: () => void
}) => {
  const [updateContact, { loading }] = useUpdateVendorContactMutation({ client: useGqlClient() })
  const [errors, setErrors] = useState<string[]>([])

  const form = useForm<ContactForm>({
    shouldUnregister: true,
    defaultValues: {
      name: vendorContact.name ?? '',
      email: vendorContact.email,
      phoneNumber: vendorContact.phoneNumber ?? '',
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async ({ name, email, phoneNumber }: ContactForm) => {
    try {
      setErrors([])

      const result = await updateContact({
        variables: {
          vendorId,
          vendorContactId: vendorContact.id,
          name,
          email,
          phoneNumber: phoneNumber || null,
        },
      })

      if (!result.data?.updateVendorContact?.id) {
        setErrors(['Could not save this contact. Please try again.'])
        return
      }

      onClose()
      onAfterSave()
    } catch (err) {
      const validationErrors = GraphQL.parseErrorList(err)
      if (validationErrors.length > 0) setErrors(validationErrors)
      else console.error(err)
    }
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      onSubmit={stopPropagate(form.handleSubmit(onSubmit))}
      title="Edit Contact"
      submitButtonShowSpinner={loading}
      submitButtonDisabled={loading}
      submitButtonText="Save"
    >
      <div className="flex flex-col gap-y-2">
        <TextField
          autoFocus
          label="Name"
          errors={form.formState.errors.name?.message}
          {...form.register('name')}
        />
        <TextField
          label="Email"
          errors={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Controller
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <PhoneInput
              value={field.value}
              onChange={field.onChange}
              label="Phone Number"
              errors={form.formState.errors.phoneNumber?.message}
            />
          )}
        />

        <p className="text-sm text-slate-500">
          Changing the email address points this vendor at that person instead. Requests you have
          already sent keep going to the old address.
        </p>

        {errors.length > 0 && (
          <ul>
            {errors.map((err) => (
              <li key={err} className="text-sm text-rose-500">
                {err}
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalForm>
  )
}

export default EditContactModal
